import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { GlobalState } from './global-state';
import { ParentMessagesSender } from './parent-messages-sender';
import { ExternalApisService } from './external-apis.service';

const ARCOPRIME_RUT = '77215640-5';

@Injectable({
  providedIn: 'root',
})
export class ParentMessagesListener implements OnDestroy {
  private lastRutInitialized: string | null = null;
  private listener = (event: MessageEvent) => {
    const message = event.data;
    const token = message.token;
    const user = message.user;
    const scrollY = message.scrollY;

    if (token) {
      this.globalState.token = token;
      this.parentMessagesSender.getUser();
    } else if (user) {
      this.globalState.user = user;
      this.initDataForUser(user);
    } else if (scrollY) {
      this.globalState.scrollY = scrollY;
    }
  };

  constructor(
    private globalState: GlobalState,
    private parentMessagesSender: ParentMessagesSender,
    private externalApis: ExternalApisService,
  ) {
    window.addEventListener('message', this.listener);
    // Fallback: if user already present, initialize; also react to future changes
    this.globalState.user$.subscribe((u) => {
      if (u) this.initDataForUser(u);
    });
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.listener);
  }

  private initDataForUser(user: any): void {
    const rut: string = (user?.rut_concesionario ?? '').toString().trim();
    if (!rut) return;
    if (this.lastRutInitialized === rut) return; // avoid duplicate calls
    this.lastRutInitialized = rut;
    if (rut === ARCOPRIME_RUT) {
      // Arcoprime: derivamos TODOS los pagadores desde user_eds (pagador = "1"+eds).
      // - `pagadores` (todos) lo usan los historiales.
      // - `pagadoresActivos` (con INSTALACIONES vigentes) lo usan las pantallas
      //   de ingreso; se calcula consultando cada pagador y esperando las N respuestas.
      this.globalState.isArcoprime = true;
      this.externalApis.getEdsPortal().subscribe((res) => {
        const userEds: string[] = res?.user_eds ?? res?.data?.user_eds ?? [];
        const pagadores = (Array.isArray(userEds) ? userEds : [])
          .map((eds) => {
            const n = parseInt(eds, 10);
            if (isNaN(n)) return null;
            const code = '1' + n; // ej: 60370 -> 160370
            return { id_pagador: code.padStart(10, '0'), label: code };
          })
          .filter(
            (p): p is { id_pagador: string; label: string } => p !== null,
          );
        this.globalState.pagadores = pagadores;
        this.computePagadoresActivos(pagadores);
      });
    } else {
      this.getCabecera(rut);
    }
  }

  private computePagadoresActivos(
    pagadores: Array<{ id_pagador: string; label: string }>,
  ): void {
    if (pagadores.length === 0) {
      this.globalState.pagadoresActivos = [];
      return;
    }
    // Una consulta por pagador; se esperan todas y se conservan las que
    // tengan INSTALACIONES vigentes. Un error individual no invalida el resto.
    this.parentMessagesSender.displayLoader();
    forkJoin(
      pagadores.map((p) =>
        this.externalApis.getInstalaciones(p.id_pagador).pipe(
          map((res) => {
            const raw = res?.INSTALACIONES ?? res?.data?.INSTALACIONES;
            return { pagador: p, activo: Array.isArray(raw) && raw.length > 0 };
          }),
          catchError(() => of({ pagador: p, activo: false })),
        ),
      ),
    )
      .pipe(finalize(() => this.parentMessagesSender.hideLoader()))
      .subscribe((resultados) => {
        this.globalState.pagadoresActivos = resultados
          .filter((r) => r.activo)
          .map((r) => r.pagador);
      });
  }

  private getCabecera(rutOrPagador: string): void {
    this.externalApis.getCabecera(rutOrPagador).subscribe((res) => {
      const pagador =
        res?.DATOS_CLIENTE?.PAGADOR ?? res?.data?.DATOS_CLIENTE?.PAGADOR ?? '';
      if (pagador) {
        this.globalState.pagador = pagador;
        this.getInstalaciones(pagador);
      }
    });
  }

  private getInstalaciones(pagador: string): void {
    this.externalApis.getInstalaciones(pagador).subscribe((res) => {
      const raw = res?.INSTALACIONES ?? res?.data?.INSTALACIONES;
      const list = Array.isArray(raw) ? raw : [];
      const tope = new Date(2025, 4, 1);
      const newEds: Array<{ label: string; value: number; desde: Date }> = [];
      list.forEach((e: any) => {
        const desdeArr = String(e?.FDESDE || '2020-01-01').split('-');
        const desdeDate = new Date(
          Number(desdeArr[0] || 0),
          Number(desdeArr[1] || 0),
          Number(desdeArr[2] || 0),
        );
        newEds.push({
          label: e?.CODIGO_DIRECCION,
          value: parseInt(e?.ID_EDS, 10),
          desde: desdeDate,
        });
      });
      this.globalState.allEds = newEds;
    });
  }
}
